import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Sparkles, CheckCircle2, Loader2, AlertTriangle } from "lucide-react";

const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 30; // ~60s

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const { refreshProStatus } = useAuth();

  const [state, setState] = useState("polling"); // polling | success | failed | expired | timeout
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }

    let cancelled = false;
    let timer;

    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          await refreshProStatus();
          setState("success");
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) {
          setState(data.payment_status);
          return;
        }
      } catch {
        // Silent; keep polling
      }
      attemptsRef.current += 1;
      if (attemptsRef.current >= MAX_ATTEMPTS) {
        setState("timeout");
        return;
      }
      timer = setTimeout(poll, POLL_INTERVAL_MS);
    };

    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <span className="grid h-9 w-9 place-items-center rounded-lg bg-blue-600 text-white">
            <Sparkles className="h-4 w-4" />
          </span>
          <span className="font-jakarta text-xl font-bold text-slate-900">AlternAI</span>
        </Link>

        <Card className="p-8 rounded-2xl border-slate-200 bg-white text-center" data-testid="payment-success-card">
          {state === "polling" && (
            <>
              <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-blue-50 text-blue-600">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
              <h1 className="mt-4 font-jakarta text-xl font-bold text-slate-900">Confirmation du paiement…</h1>
              <p className="mt-2 text-sm text-slate-500">Merci, nous vérifions ton paiement auprès de Stripe.</p>
            </>
          )}
          {state === "success" && (
            <>
              <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-emerald-50 text-emerald-600">
                <CheckCircle2 className="h-6 w-6" />
              </div>
              <h1 className="mt-4 font-jakarta text-2xl font-bold text-slate-900">Bienvenue chez AlternAI Pro 🎉</h1>
              <p className="mt-2 text-sm text-slate-600">Ton accès Pro est activé. Tu peux générer tes documents de candidature dès maintenant.</p>
              <Button onClick={() => navigate("/dashboard")} className="mt-6 w-full h-11 bg-blue-600 hover:bg-blue-700 rounded-full" data-testid="payment-success-cta">
                Aller à mon dashboard
              </Button>
            </>
          )}
          {(state === "failed" || state === "expired") && (
            <>
              <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-rose-50 text-rose-600">
                <AlertTriangle className="h-6 w-6" />
              </div>
              <h1 className="mt-4 font-jakarta text-xl font-bold text-slate-900">
                {state === "expired" ? "Session expirée" : "Paiement non abouti"}
              </h1>
              <p className="mt-2 text-sm text-slate-500">Aucun montant n'a été prélevé. Tu peux réessayer quand tu le souhaites.</p>
              <Button onClick={() => navigate("/dashboard")} variant="outline" className="mt-6 w-full h-11 rounded-full">Retour au dashboard</Button>
            </>
          )}
          {state === "timeout" && (
            <>
              <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-amber-50 text-amber-600">
                <Loader2 className="h-6 w-6" />
              </div>
              <h1 className="mt-4 font-jakarta text-xl font-bold text-slate-900">Ça prend un peu plus de temps…</h1>
              <p className="mt-2 text-sm text-slate-500">Le paiement est en cours de traitement par la banque. Ton accès Pro sera activé automatiquement dès la confirmation.</p>
              <Button onClick={() => navigate("/dashboard")} className="mt-6 w-full h-11 bg-blue-600 hover:bg-blue-700 rounded-full">Retour au dashboard</Button>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
